import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  IconButton,
  Modal,
  NumberInput,
  TextInput,
  ToggleInput,
  useToast,
} from "@a2z/ui";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { queryKeys } from "@/lib/query/core/query-keys";
import { pickLocalized } from "@/lib/i18n/localized";
import type { ProductVariantView, ProductView } from "@/lib/api/types";
import {
  findColorGroup,
  findSizeGroup,
  swatchHex,
} from "@/modules/products/lib/variantsDisplay";
import {
  useBulkDeleteVariantsMutation,
  useUpdateOptionValueMutation,
  useUpdateVariantMutation,
} from "@/modules/products/services/useVariantMutations";
import { useVariantCostsQuery } from "@/modules/products/services/useVariantCostsQuery";

type Mode = "stock" | "full";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mode: Mode;
  storeSlug: string;
  product: ProductView;
  currency?: string;
};

type ColorDraft = { valueAr: string; valueEn: string; colorHex: string };

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Parse a discount input → a non-negative number, or null when blank/invalid. */
function parseDiscount(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Parse a cost input → a non-negative number, or null when blank/invalid. */
function parseCost(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function ProductVariantsEditorSheet({
  isOpen,
  onClose,
  mode,
  storeSlug,
  product,
  currency = "EGP",
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateVariant } = useUpdateVariantMutation();
  const { mutateAsync: updateOptionValue } = useUpdateOptionValueMutation();
  const { mutateAsync: bulkDeleteVariants } = useBulkDeleteVariantsMutation();

  const colorGroup = findColorGroup(product);
  const sizeGroup = findSizeGroup(product);

  // ── Drafts (seeded once per mount; the sheet remounts each open) ─────────────
  const [stock, setStock] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      product.variants.map((v) => [v.id, String(v.stockCount)]),
    ),
  );
  const [price, setPrice] = useState<Record<string, string>>(() =>
    Object.fromEntries(product.variants.map((v) => [v.id, String(v.price)])),
  );
  const [discount, setDiscount] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      product.variants.map((v) => [
        v.id,
        v.discountedPrice != null ? String(v.discountedPrice) : "",
      ]),
    ),
  );
  const [discountOn, setDiscountOn] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      product.variants.map((v) => [v.id, v.discountedPrice != null]),
    ),
  );
  const [active, setActive] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(product.variants.map((v) => [v.id, v.isActive])),
  );
  const [colors, setColors] = useState<Record<string, ColorDraft>>(() =>
    Object.fromEntries(
      (colorGroup?.values ?? []).map((v) => [
        v.id,
        {
          valueAr: v.valueAr,
          valueEn: v.valueEn ?? "",
          colorHex: swatchHex(v),
        },
      ]),
    ),
  );
  const [sizes, setSizes] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (sizeGroup?.values ?? []).map((v) => [v.id, v.valueEn || v.valueAr]),
    ),
  );

  // Seller-only goods cost — fetched separately (it's deliberately off the
  // buyer-shared variant view) and seeded into a draft map once it arrives.
  const { data: variantCosts } = useVariantCostsQuery({
    storeSlug,
    productId: product.id,
    enabled: mode === "full",
  });
  const initialCost = useMemo(
    () =>
      new Map<string, number | null>(
        (variantCosts ?? []).map((c) => [c.variantId, c.cost]),
      ),
    [variantCosts],
  );
  const [cost, setCost] = useState<Record<string, string>>({});
  const [costSeeded, setCostSeeded] = useState(false);
  if (!costSeeded && variantCosts) {
    setCostSeeded(true);
    setCost(
      Object.fromEntries(
        variantCosts.map((c) => [
          c.variantId,
          c.cost != null ? String(c.cost) : "",
        ]),
      ),
    );
  }

  const [deleted, setDeleted] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const liveVariants = product.variants.filter((v) => !deleted.has(v.id));

  const variantLabel = (v: ProductVariantView): string => {
    const parts = v.selections.map((s) =>
      pickLocalized(locale, s.optionValueAr, s.optionValueEn),
    );
    return (
      parts.filter(Boolean).join(" · ") || t(keys.products.variantDefaultLabel)
    );
  };
  const variantSwatch = (v: ProductVariantView): string | null => {
    const colorSel = colorGroup
      ? v.selections.find((s) => s.optionGroupId === colorGroup.id)
      : undefined;
    if (!colorSel) return null;
    // Prefer the in-progress colour draft so edits preview live.
    return (
      colors[colorSel.optionValueId]?.colorHex ??
      swatchHex({
        colorHex: colorSel.optionValueColorHex,
        valueEn: colorSel.optionValueEn,
        valueAr: colorSel.optionValueAr,
      })
    );
  };

  const isDirty = useMemo(() => {
    if (deleted.size > 0) return true;
    for (const v of product.variants) {
      if (Number(stock[v.id]) !== v.stockCount) return true;
      if (mode === "full") {
        if (Number(price[v.id]) !== v.price) return true;
        const nextDiscount =
          (discountOn[v.id] ?? false)
            ? parseDiscount(discount[v.id] ?? "")
            : null;
        if (nextDiscount !== (v.discountedPrice ?? null)) return true;
        if (active[v.id] !== v.isActive) return true;
        if (costSeeded) {
          const nextCost = parseCost(cost[v.id] ?? "");
          if (nextCost !== (initialCost.get(v.id) ?? null)) return true;
        }
      }
    }
    if (mode === "full") {
      for (const cv of colorGroup?.values ?? []) {
        const d = colors[cv.id];
        if (!d) continue;
        if (d.valueAr.trim() !== cv.valueAr) return true;
        if ((d.valueEn.trim() || "") !== (cv.valueEn ?? "")) return true;
        if (d.colorHex !== swatchHex(cv)) return true;
      }
      for (const sv of sizeGroup?.values ?? []) {
        if ((sizes[sv.id] ?? "").trim() !== (sv.valueEn || sv.valueAr))
          return true;
      }
    }
    return false;
  }, [
    stock,
    price,
    discount,
    discountOn,
    active,
    cost,
    costSeeded,
    initialCost,
    colors,
    sizes,
    deleted,
    mode,
    product.variants,
    colorGroup,
    sizeGroup,
  ]);

  const handleSave = async () => {
    if (!isDirty || saving) return;
    // Cost may equal the price (break-even) but never exceed it.
    if (mode === "full" && costSeeded) {
      for (const v of product.variants) {
        if (deleted.has(v.id)) continue;
        const nextPriceValue = Number(price[v.id]);
        const effectivePrice = Number.isFinite(nextPriceValue)
          ? nextPriceValue
          : v.price;
        const nextCost = parseCost(cost[v.id] ?? "");
        if (nextCost !== null && nextCost > effectivePrice) {
          toast.error(t(keys.errors.costMustNotExceedPrice));
          return;
        }
      }
    }
    setSaving(true);
    try {
      if (mode === "full") {
        // Option-value labels/hex first (shared across variants).
        for (const cv of colorGroup?.values ?? []) {
          const d = colors[cv.id];
          if (!d) continue;
          const hex = HEX_RE.test(d.colorHex) ? d.colorHex : null;
          const changed =
            d.valueAr.trim() !== cv.valueAr ||
            (d.valueEn.trim() || "") !== (cv.valueEn ?? "") ||
            d.colorHex !== swatchHex(cv);
          if (changed) {
            await updateOptionValue({
              storeSlug,
              productId: product.id,
              valueId: cv.id,
              fields: {
                valueAr: d.valueAr.trim(),
                valueEn: d.valueEn.trim() || null,
                colorHex: hex,
              },
            });
          }
        }
        for (const sv of sizeGroup?.values ?? []) {
          const label = (sizes[sv.id] ?? "").trim();
          if (label && label !== (sv.valueEn || sv.valueAr)) {
            // Size keeps EN === AR per the agreed model.
            await updateOptionValue({
              storeSlug,
              productId: product.id,
              valueId: sv.id,
              fields: { valueAr: label, valueEn: label },
            });
          }
        }
      }

      // Per-variant field changes.
      for (const v of product.variants) {
        if (deleted.has(v.id)) continue;
        const fields: {
          stockCount?: number;
          price?: number;
          discountedPrice?: number | null;
          cost?: number | null;
          isActive?: boolean;
        } = {};
        const nextStock = Number(stock[v.id]);
        if (
          Number.isFinite(nextStock) &&
          nextStock >= 0 &&
          nextStock !== v.stockCount
        ) {
          fields.stockCount = Math.floor(nextStock);
        }
        if (mode === "full") {
          const nextPrice = Number(price[v.id]);
          if (
            Number.isFinite(nextPrice) &&
            nextPrice >= 0 &&
            nextPrice !== v.price
          ) {
            fields.price = nextPrice;
          }
          const nextDiscount =
            (discountOn[v.id] ?? false)
              ? parseDiscount(discount[v.id] ?? "")
              : null;
          if (nextDiscount !== (v.discountedPrice ?? null)) {
            fields.discountedPrice = nextDiscount;
          }
          if (active[v.id] !== v.isActive) {
            fields.isActive = active[v.id];
          }
          if (costSeeded) {
            const nextCost = parseCost(cost[v.id] ?? "");
            if (nextCost !== (initialCost.get(v.id) ?? null)) {
              fields.cost = nextCost;
            }
          }
        }
        if (Object.keys(fields).length > 0) {
          await updateVariant({
            storeSlug,
            productId: product.id,
            variantId: v.id,
            fields,
          });
        }
      }

      // Deletions last — one transactional bulk call (soft delete; restores the
      // base default automatically if this empties the variant set).
      if (deleted.size > 0) {
        await bulkDeleteVariants({
          storeSlug,
          productId: product.id,
          variantIds: Array.from(deleted),
        });
      }

      // Costs live in their own query; refetch so a reopen shows fresh values.
      if (mode === "full") {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.seller.productVariantCosts(storeSlug, product.id),
          exact: true,
        });
      }

      toast.success(
        mode === "stock"
          ? t(keys.products.toastStockUpdated)
          : t(keys.products.variantsSaved),
      );
      onClose();
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "stock"
      ? t(keys.products.editStockTitle)
      : t(keys.products.manageVariantsTitle);
  const subtitle =
    mode === "stock"
      ? t(keys.products.editStockSubtitle, {
          name: pickLocalized(locale, product.nameAr, product.nameEn),
        })
      : t(keys.products.manageVariantsSubtitle, {
          name: pickLocalized(locale, product.nameAr, product.nameEn),
        });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      icon={<StockIcon />}
      title={title}
      subtitle={subtitle}
      closeLabel={t(keys.common.close)}
      footerActions={
        <>
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={saving}
          >
            {t(keys.common.cancel)}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => void handleSave()}
            isLoading={saving}
            disabled={!isDirty}
          >
            {t(keys.common.save)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col">
        {mode === "full" ? (
          <div className="flex flex-col gap-5">
            {colorGroup && colorGroup.values.length > 0 ? (
              <section className="flex flex-col gap-2">
                <SectionLabel>
                  {t(keys.products.variantsColorsSection)}
                </SectionLabel>
                <div className="flex flex-col gap-2.5">
                  {colorGroup.values.map((cv) => {
                    const d = colors[cv.id];
                    if (!d) return null;
                    return (
                      <div
                        key={cv.id}
                        className="flex flex-col gap-2.5 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-2.5"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={
                              HEX_RE.test(d.colorHex) ? d.colorHex : "#888888"
                            }
                            onChange={(e) =>
                              setColors((c) => ({
                                ...c,
                                [cv.id]: { ...d, colorHex: e.target.value },
                              }))
                            }
                            className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] p-0"
                            aria-label={t(keys.products.colorSwatchLabel)}
                          />
                          <input
                            value={d.colorHex}
                            onChange={(e) =>
                              setColors((c) => ({
                                ...c,
                                [cv.id]: { ...d, colorHex: e.target.value },
                              }))
                            }
                            placeholder="#RRGGBB"
                            aria-label={t(keys.products.colorHexHint)}
                            className="h-9 w-[110px] rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] px-2 font-mono text-[12.5px] focus:outline-none"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <TextInput
                            name={`color-en-${cv.id}`}
                            value={d.valueEn}
                            placeholder={t(keys.products.colorNameEn)}
                            onChange={(e) =>
                              setColors((c) => ({
                                ...c,
                                [cv.id]: { ...d, valueEn: e.target.value },
                              }))
                            }
                          />
                          <TextInput
                            name={`color-ar-${cv.id}`}
                            dir="rtl"
                            value={d.valueAr}
                            placeholder={t(keys.products.colorNameAr)}
                            onChange={(e) =>
                              setColors((c) => ({
                                ...c,
                                [cv.id]: { ...d, valueAr: e.target.value },
                              }))
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {sizeGroup && sizeGroup.values.length > 0 ? (
              <section className="flex flex-col gap-2">
                <SectionLabel>
                  {t(keys.products.variantsSizesSection)}
                </SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {sizeGroup.values.map((sv) => (
                    <div key={sv.id} className="w-[64px]">
                      <TextInput
                        name={`size-${sv.id}`}
                        value={sizes[sv.id] ?? ""}
                        onChange={(e) =>
                          setSizes((s) => ({ ...s, [sv.id]: e.target.value }))
                        }
                        className="text-center font-mono"
                      />
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="flex flex-col gap-2">
              <SectionLabel>
                {t(keys.products.variantsListSection)}
              </SectionLabel>
              <VariantRows
                variants={liveVariants}
                mode="full"
                variantLabel={variantLabel}
                variantSwatch={variantSwatch}
                stock={stock}
                setStock={setStock}
                price={price}
                setPrice={setPrice}
                discount={discount}
                setDiscount={setDiscount}
                discountOn={discountOn}
                setDiscountOn={setDiscountOn}
                active={active}
                setActive={setActive}
                cost={cost}
                setCost={setCost}
                currency={currency}
                // Any real (selection-bearing) variant is deletable — even the
                // last one: the backend restores the hidden base default when
                // the set is emptied. The base sentinel itself is gated per-row
                // (it has no selections) so it's never directly deletable.
                canDelete
                onDelete={(id) => setDeleted((d) => new Set(d).add(id))}
                t={t}
              />
            </section>
          </div>
        ) : (
          <VariantRows
            variants={liveVariants}
            mode="stock"
            variantLabel={variantLabel}
            variantSwatch={variantSwatch}
            stock={stock}
            setStock={setStock}
            price={price}
            setPrice={setPrice}
            discount={discount}
            setDiscount={setDiscount}
            discountOn={discountOn}
            setDiscountOn={setDiscountOn}
            active={active}
            setActive={setActive}
            cost={cost}
            setCost={setCost}
            currency={currency}
            canDelete={false}
            onDelete={() => {}}
            t={t}
          />
        )}
      </div>
    </Modal>
  );
}

function StockIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5M12 22V13" />
    </svg>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">
      {children}
    </span>
  );
}

function TrashIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

type RowsProps = {
  variants: ProductVariantView[];
  mode: Mode;
  variantLabel: (v: ProductVariantView) => string;
  variantSwatch: (v: ProductVariantView) => string | null;
  stock: Record<string, string>;
  setStock: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  price: Record<string, string>;
  setPrice: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  discount: Record<string, string>;
  setDiscount: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  discountOn: Record<string, boolean>;
  setDiscountOn: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  active: Record<string, boolean>;
  setActive: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  cost: Record<string, string>;
  setCost: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  currency: string;
  canDelete: boolean;
  onDelete: (id: string) => void;
  t: (key: string, values?: Record<string, string | number>) => string;
};

function VariantRows({
  variants,
  mode,
  variantLabel,
  variantSwatch,
  stock,
  setStock,
  price,
  setPrice,
  discount,
  setDiscount,
  discountOn,
  setDiscountOn,
  active,
  setActive,
  cost,
  setCost,
  currency,
  canDelete,
  onDelete,
  t,
}: RowsProps) {
  if (variants.length === 0) {
    return (
      <p className="py-6 text-center text-[13px] text-[color:var(--text-muted)]">
        {t(keys.products.variantsNoneYet)}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {variants.map((v) => {
        const swatch = variantSwatch(v);
        return (
          <li
            key={v.id}
            className="flex flex-col gap-2.5 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-3"
          >
            {/* Header — label, and in full mode the visibility toggle + delete */}
            <div className="flex items-center gap-2">
              {swatch ? (
                <span
                  aria-hidden="true"
                  className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-black/10"
                  style={{ background: swatch }}
                />
              ) : null}
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[color:var(--text-primary)]">
                {variantLabel(v)}
              </span>
              {mode === "full" ? (
                <>
                  <label className="flex shrink-0 items-center gap-1.5">
                    <span className="text-[11px] text-[color:var(--text-muted)]">
                      {t(keys.products.variantVisibleLabel)}
                    </span>
                    <ToggleInput
                      name={`vactive-${v.id}`}
                      checked={active[v.id] ?? true}
                      onChange={(e) =>
                        setActive((a) => ({ ...a, [v.id]: e.target.checked }))
                      }
                      ariaLabel={t(keys.products.variantVisibleLabel)}
                    />
                  </label>
                  <IconButton
                    variant="danger"
                    size="md"
                    disabled={!canDelete || v.selections.length === 0}
                    onClick={() => onDelete(v.id)}
                    ariaLabel={t(keys.products.removeVariantAria, {
                      name: variantLabel(v),
                    })}
                    className="shrink-0 !border-transparent hover:bg-[color:var(--error-50)] disabled:opacity-40"
                    icon={<TrashIcon />}
                  />
                </>
              ) : null}
            </div>

            {mode === "full" ? (
              <>
                {/* Price + Stock */}
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] text-[color:var(--text-muted)]">
                      {t(keys.products.priceWithCurrency, { currency })}
                    </span>
                    <NumberInput
                      name={`vprice-${v.id}`}
                      allowDecimal
                      min={0}
                      value={price[v.id] ?? ""}
                      onChange={(e) =>
                        setPrice((p) => ({ ...p, [v.id]: e.target.value }))
                      }
                      className="font-mono"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] text-[color:var(--text-muted)]">
                      {t(keys.products.stockLabel)}
                    </span>
                    <NumberInput
                      name={`vstock-${v.id}`}
                      min={0}
                      value={stock[v.id] ?? ""}
                      onChange={(e) =>
                        setStock((s) => ({ ...s, [v.id]: e.target.value }))
                      }
                    />
                  </label>
                </div>

                {/* Cost — seller-only goods cost (for profit/margin) */}
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-[color:var(--text-muted)]">
                    {t(keys.products.variantCostLabel)} ({currency})
                  </span>
                  <span className="w-[160px]">
                    <NumberInput
                      name={`vcost-${v.id}`}
                      allowDecimal
                      min={0}
                      value={cost[v.id] ?? ""}
                      onChange={(e) =>
                        setCost((c) => ({ ...c, [v.id]: e.target.value }))
                      }
                      placeholder="—"
                      className="font-mono"
                    />
                  </span>
                  <span className="text-[11px] text-[color:var(--text-muted)]">
                    {t(keys.products.variantCostHint)}
                  </span>
                </label>

                {/* Discount — opt-in toggle reveals the price-after-discount field */}
                <div className="flex flex-col gap-2 border-t border-[color:var(--border-light)] pt-2.5">
                  <label className="flex items-center gap-2">
                    <ToggleInput
                      name={`vsale-${v.id}`}
                      checked={discountOn[v.id] ?? false}
                      onChange={(e) =>
                        setDiscountOn((d) => ({
                          ...d,
                          [v.id]: e.target.checked,
                        }))
                      }
                      ariaLabel={t(keys.products.variantOnSale)}
                    />
                    <span className="text-[13px] font-medium text-[color:var(--text-primary)]">
                      {t(keys.products.variantOnSale)}
                    </span>
                  </label>
                  {discountOn[v.id] ? (
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] text-[color:var(--text-muted)]">
                        {t(keys.products.priceAfterDiscount)} ({currency})
                      </span>
                      <span className="w-[160px]">
                        <NumberInput
                          name={`vdiscount-${v.id}`}
                          allowDecimal
                          min={0}
                          value={discount[v.id] ?? ""}
                          onChange={(e) =>
                            setDiscount((d) => ({
                              ...d,
                              [v.id]: e.target.value,
                            }))
                          }
                          placeholder="—"
                          className="font-mono"
                        />
                      </span>
                    </label>
                  ) : null}
                </div>
              </>
            ) : (
              /* Stock mode — just the per-variant stock field */
              <label className="flex items-center justify-between gap-3">
                <span className="text-[13px] text-[color:var(--text-secondary)]">
                  {t(keys.products.stockLabel)}
                </span>
                <span className="w-[120px]">
                  <NumberInput
                    name={`vstock-${v.id}`}
                    min={0}
                    value={stock[v.id] ?? ""}
                    onChange={(e) =>
                      setStock((s) => ({ ...s, [v.id]: e.target.value }))
                    }
                    className="text-center"
                  />
                </span>
              </label>
            )}
          </li>
        );
      })}
    </ul>
  );
}
